import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  IconButton,
  Collapse,
  Avatar,
} from "@mui/material";
import {
  Notifications,
  Menu,
  DashboardOutlined,
  ExpandLess,
  ExpandMore,
  Storage,
  ShoppingCart,
  BusinessCenter,
  Apartment,
  HandshakeOutlined,
  AttachMoneyOutlined,
  BusinessCenterOutlined,
  ChatBubbleOutline,
  EventNoteOutlined,
  GroupOutlined,
  History,
  InsertDriveFileOutlined,
  ChevronLeft,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getUser } from "../../app/auth/services/authService.tsx";
import {
  fetchEmployees,
  getUserInfoByEmail,
} from "../../app/data_management/services/employeeService.tsx";
import { getProfileData, saveProfileData } from "../../app/user_profile/services/userService.tsx";

const Sidebar = () => {
  const [open, setOpen] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const [user, setUser] = useState({
    user: {
      username: "",
      email: "",
    },
    roles: [],
  });

  const handleProfileClick = async () => {
    try {
      if (!user?.user.email) return;
      
      const profileData = await getProfileData(user.user.email);
      saveProfileData(profileData);
      navigate('/user-profile', { state: profileData });
    } catch (error) {
      console.error("Failed to load profile:", error);
      // Fallback to empty state
      navigate('/user-profile');
    }
  };
  

  const handleClick = (menu: string) => {
    setOpen(open === menu ? null : menu);
    setActive(menu);
  };

  const handleActive = (menu: string) => {
    setActive(menu);
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const navigate = useNavigate();

  const menuItems = [
    {
      title: "Dashboard",
      icon: <DashboardOutlined sx={{ color: "#878787" }} />,
      submenu: ["Default", "Project", "Sales", "Building Management"],
    },
    {
      title: "Data Management",
      icon: <Storage sx={{ color: "#878787" }} />,
      submenu: [
        { title: "Item", link: "/data-management/item" },
        { title: "Pegawai", link: "/data-management/employee" },
        { title: "Absensi", link: "/data-management/attendance" },
        { title: "Office", link: "/data-management/office" },
        { title: "Vendor", link: "/data-management/vendor" },
      ],
    },
    {
      title: "Sales",
      icon: <HandshakeOutlined sx={{ color: "#878787" }} />,
      submenu: [
        { title: "Analisa Harga Satuan", link: "/sales/ahs" },
        { title: "Project Listing", link: "/sales/project" },
        { title: "RAB", link: "/sales/rab" },
        { title: "Bidding Process", link: "/sales/bidding" },
      ],
    },
    {
      title: "Project Management",
      icon: <AttachMoneyOutlined sx={{ color: "#878787" }} />,
      submenu: [
        { title: "Lelang & Kontrak", link: "/project-management/lelang" },
        { title: "RAP", link: "/project-management/rap" },
        { title: "Rolling Forecast", link: "/project-management/rolling-forecast" },
        { title: "Project Management", link: "/project-management/project-management-report" },
      ],
    },
    {
      title: "Building Management",
      icon: <BusinessCenterOutlined sx={{ color: "#878787" }} />,
      submenu: [
        { title: "Project Location Details", link: "/building-management/project-detail" },
        { title: "TAD Payroll Processing", link: "/building-management/payroll" },
        { title: "Building Survey", link: "/building-management/survey" },
        { title: "Building Repair", link: "/building-management/repair" },
        { title: "RAB Building", link: "/building-management/rab" },
        { title: "RAB Absorption", link: "/building-management/rab-absorption" },
        // { title: "Workspace Readiness", link: "/building-management/workspace-readiness" },
        { title: "Workspace Readiness", link: "/building-management/workspace" },
        { title: "Inventory", link: "/building-management/inventory" },
        { title: "Building Assessment", link: "/building-management/building-assessment" },
      ],
    },
    
  ];

  const bottomMenuItems = [
    {
      title: "Messages",
      icon: <ChatBubbleOutline sx={{ color: "#878787" }} />,
    },
    {
      title: "Role Assignment",
      icon: <EventNoteOutlined sx={{ color: "#878787" }} />,
      link: "/role",
    },
    {
      title: "User Management",
      icon: <GroupOutlined sx={{ color: "#878787" }} />,
    },
    {
      title: "Audit Trail",
      icon: <History sx={{ color: "#878787" }} />,
    },
    {
      title: "File Management",
      icon: <InsertDriveFileOutlined sx={{ color: "#878787" }} />,
    },
  ];

  const fetchUser = async () => {
    try {
      const response = await getUser(); // Make sure this is the API call
      await localStorage.setItem("username", response.user.username);
      await localStorage.setItem("email", response.user.email);
      setUser(response);
    } catch (error) {
      console.error("Failed to fetch user:", error);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? 80 : 280,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: collapsed ? 80 : 280,
          boxSizing: "border-box",
          background: "white",
          borderRadius: "12px", // Menjadikan sudut lebih membulat
          border: "1px solid #EDEDED",
          p: 1.5,
          m: 1,
          transition: "width 0.3s ease",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Efek shadow lembut
        },
      }}
    >
      {/* Header Sidebar */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent={collapsed ? "center" : "space-between"}
        mb={2}
        px={collapsed ? 0 : 1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {/* Logo dari Folder img */}
          {!collapsed && (
            <Box
              component="img"
              src="/img/LOGO_SINERGISPP.png"
              alt="Logo Sinergi SPP"
              sx={{
                width: 28,
                height: 28,
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid #EDEDED",
              }}
            />
          )}
          {!collapsed && (
            <Typography fontSize={15} fontWeight={600} color="#101010">
              SIMPRO
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={1}>
          {!collapsed && (
            <IconButton>
              <Notifications sx={{ color: "#383838" }} />
            </IconButton>
          )}
          <IconButton onClick={toggleCollapse}>
            {collapsed ? (
              <Menu sx={{ color: "#383838" }} />
            ) : (
              <ChevronLeft sx={{ color: "#383838" }} />
            )}
          </IconButton>
        </Box>
      </Box>
      {/* Menu List */}
      <List sx={{ width: "100%", bgcolor: "white", p: 0 }}>
        {menuItems.map((menu) => {
          const isActive = active === menu.title;

          return (
            <Box key={menu.title}>
              <ListItem
                sx={{
                  bgcolor: isActive ? "#C5EED8" : "inherit",
                  borderRadius: 1,
                  mb: 0.5,
                  p: 0,
                }}
              >
                <ListItemButton
                  onClick={() =>
                    menu.submenu
                      ? handleClick(menu.title)
                      : handleActive(menu.title)
                  }
                  sx={{
                    "&:hover": !isActive && { bgcolor: "#F0F0F0" },
                    p: "8px 12px",
                  }}
                >
                  {menu.icon && (
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {menu.icon}
                    </ListItemIcon>
                  )}
                  {!collapsed && (
                    <ListItemText
                      primary={menu.title}
                      sx={{ color: "#121619" }}
                    />
                  )}
                  {menu.submenu &&
                    !collapsed &&
                    (open === menu.title ? (
                      <ExpandLess sx={{ color: "#8E8E8E" }} />
                    ) : (
                      <ExpandMore sx={{ color: "#8E8E8E" }} />
                    ))}
                </ListItemButton>
              </ListItem>
              {menu.submenu && (
                <Collapse
                  in={open === menu.title && !collapsed}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding sx={{ pl: 4 }}>
                    {menu.submenu.map((sub) => {
                      const isSubActive =
                        active === (typeof sub === "string" ? sub : sub.title);
                      return (
                        <ListItem
                          key={typeof sub === "string" ? sub : sub.title}
                          sx={{
                            bgcolor: isSubActive ? "#F5F5F5" : "inherit",
                            borderRadius: 1,
                            mb: 0.5,
                            p: 0,
                          }}
                        >
                          <ListItemButton
                            onClick={() =>
                              handleActive(
                                typeof sub === "string" ? sub : sub.title
                              )
                            }
                            sx={{
                              "&:hover": !isSubActive && { bgcolor: "#F0F0F0" },
                              p: "6px 12px",
                            }}
                          >
                            {typeof sub === "string" ? (
                              <ListItemText
                                primary={sub}
                                sx={{ color: "#101010", pl: 1 }}
                              />
                            ) : (
                              <Link
                                to={sub.link}
                                style={{
                                  textDecoration: "none",
                                  color: "inherit",
                                  width: "100%",
                                }}
                              >
                                <ListItemText
                                  primary={sub.title}
                                  sx={{ color: "#101010", pl: 1 }}
                                />
                              </Link>
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>
      {/* Bottom Menu List */}
      {!collapsed && (
        <Typography sx={{ textAlign: "left", mt: 5, pl: 2, color: "inherit" }}>
          Tools
        </Typography>
      )}
      <List sx={{ width: "100%", bgcolor: "white", p: 0 }}>
        {bottomMenuItems.map((menu) => {
          const isActive = active === menu.title;

          return (
            <ListItem
              key={menu.title}
              sx={{
                bgcolor: isActive ? "#C5EED8" : "inherit",
                borderRadius: 1,
                mb: 0.5,
                p: 0,
              }}
            >
              <Link
                to={menu.link}
                style={{
                  textDecoration: "none",
                  width: "100%",
                  color: "inherit",
                }}
                onClick={() => handleActive(menu.title)}
              >
                <ListItemButton
                  sx={{
                    "&:hover": !isActive && { bgcolor: "#F0F0F0" },
                    p: "8px 12px",
                  }}
                >
                  {menu.icon && (
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {menu.icon}
                    </ListItemIcon>
                  )}
                  {!collapsed && (
                    <ListItemText
                      primary={menu.title}
                      sx={{ color: "#121619" }}
                    />
                  )}
                </ListItemButton>
              </Link>
            </ListItem>
          );
        })}
      </List>
      {/* User Profile */}
      <Box mt="auto" p={2} display="flex" alignItems="center" gap={2}>
        <Avatar src="/img/user-avatar.png" sx={{ width: 40, height: 40 }} />
        {!collapsed && (
          <Link
            to="/user-profile"
            style={{
              textDecoration: "none",
              color: "inherit",
              flexGrow: 1,
            }}
            onClick={handleProfileClick}
          >
            <Box>
              <Typography fontSize={14} fontWeight={600}>
                {user?.user?.username || "Loading..."}
              </Typography>
              <Typography fontSize={12} color="gray">
                {user?.user?.email || "Loading..."}
              </Typography>
            </Box>
          </Link>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
